/**
 * Sending a customer on, and saying where they land: the front or the back.
 *
 * - A **service** is a transfer: the customer joins that service's line.
 * - A **desk** or a **colleague** is a park into that desk's or that person's pool:
 *   the same piece of work, finished by somebody else.
 */
import { t, type StaffLanguage } from './staffText'
import type { CounterView, Ticket } from './types'

export type SendTarget = 'service' | 'desk' | 'colleague'

export const SEND_TARGETS: readonly SendTarget[] = ['service', 'desk', 'colleague']

/** Where a customer lands: the front or the back, everywhere (owner, 5 Nov 2026). */
export type Placement = 'front' | 'back'

export function targetLabel(target: SendTarget, lang: StaffLanguage): string {
  return t(`floor.target.${target}`, lang)
}

/** Every Place in line offers these, in this order. */
export const PLACEMENTS: readonly Placement[] = ['front', 'back']

/** The default everywhere: the back of the line (owner, 31 Oct). */
export const DEFAULT_PLACEMENT: Placement = 'back'

export function placementLabel(placement: Placement, lang: StaffLanguage): string {
  return t(`floor.place.${placement}`, lang)
}

/** What the server is sent: nothing for the back, which it takes as the default. */
export function placementBody(placement: Placement): { placement?: Placement } {
  return placement === 'front' ? { placement: 'front' } : {}
}

/** The request that sends a customer to a target. A service is a transfer; a desk
 *  or a colleague is a park into their pool — see the top of this file. */
export type SendRequest =
  | { action: 'transfer'; body: { serviceId: string; placement?: Placement } }
  | {
      action: 'park'
      body: {
        scope: 'desk' | 'staff'
        counterId?: string
        staffId?: string
        placement?: Placement
        returnToMe?: true
      }
    }

/** `returnToMe` (Send back to me) is sent only when asked for, and only on a send
 *  to a desk or a colleague; a transfer carries it through cashier.transferBody. */
export function sendRequest(
  target: SendTarget,
  destinationId: string,
  placement: Placement,
  returnToMe = false,
): SendRequest {
  const placed = placementBody(placement)
  const back = returnToMe ? { returnToMe: true as const } : {}
  switch (target) {
    case 'service':
      return { action: 'transfer', body: { serviceId: destinationId, ...placed } }
    case 'desk':
      return { action: 'park', body: { scope: 'desk', counterId: destinationId, ...placed, ...back } }
    case 'colleague':
      return { action: 'park', body: { scope: 'staff', staffId: destinationId, ...placed, ...back } }
  }
}

/** A person a customer can be sent to, as GET /branches/{id}/colleagues answers. */
export interface Colleague {
  id: string
  displayName: string
}

/** The desks a customer may be sent to: every desk in the branch but this one,
 *  open or not. A closed desk is offered on purpose — the customer waits in its
 *  pool until somebody signs in there, and the floor shows them waiting — because
 *  refusing it would send the clerk looking for a colleague who is on a break. */
export function deskChoices(counters: readonly CounterView[], selfCounterId: string): CounterView[] {
  return counters
    .filter((c) => c.counterId !== selfCounterId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
}

/** The colleagues a customer may be sent to: everybody but the clerk sending them,
 *  who would be parking the customer for themselves under another name. */
export function colleagueChoices(colleagues: readonly Colleague[], selfStaffId: string): Colleague[] {
  return colleagues.filter((c) => c.id !== selfStaffId)
}

/**
 * Who a parked customer is waiting for, in the words the operator would use.
 *
 * Named by the desk or the person, because "for Counter 02" and "for Aysel" are
 * things the person standing nearby can act on where "desk pool" is not. A person
 * is named from the colleagues list, or from the desk they are signed in at when
 * the list has not arrived.
 */
export function waitingFor(
  ticket: Ticket,
  counterId: string,
  staffId: string,
  counters: readonly CounterView[] | undefined,
  colleagues: readonly Colleague[] | undefined,
  lang: StaffLanguage,
): string {
  counters ??= []
  colleagues ??= []
  if (ticket.parkScope === 'staff') {
    if (staffId && ticket.poolStaffId === staffId) return t('floor.forYou', lang)
    const name =
      colleagues.find((c) => c.id === ticket.poolStaffId)?.displayName ??
      counters.find((c) => c.staffId && c.staffId === ticket.poolStaffId)?.staffName
    return name ? t('floor.forName', lang, { name }) : t('floor.forColleague', lang)
  }
  if (ticket.parkScope === 'desk') {
    if (counterId && ticket.poolCounterId === counterId) return t('floor.forYou', lang)
    const desk = counters.find((c) => c.counterId === ticket.poolCounterId)
    return desk ? t('floor.forName', lang, { name: desk.name }) : t('floor.forAnotherDesk', lang)
  }
  return t('floor.forAnyDesk', lang)
}
